import {
  MobileTypographyVariant,
  TypographyShade,
  TypographyVariant,
} from '../../components/typography/variants';
import {colorPrimitive} from '../../styles/primitives/colorPrimitive';

interface TypographyDefinition {
  fontSize: string;
  lineHeight: string;
  fontWeight: number;
}

interface MobileTypographyDefinition {
  fontSize: number;
  lineHeight: number;
  fontWeight: number;
}

export type TypographySizeSemanticStyles = Record<TypographyVariant, TypographyDefinition>;
export type MobileTypographySizeSemanticsStyles = Record<MobileTypographyVariant, MobileTypographyDefinition>;
export type TypographyShadeSemanticStyles = Record<TypographyShade, string>;

export interface TypographySemanticStyles extends TypographySizeSemanticStyles {
  shades: TypographyShadeSemanticStyles;
}

export interface MobileTypographySemanticStyles extends MobileTypographySizeSemanticsStyles {
  shades: TypographyShadeSemanticStyles;
}

const typographySizeSemanticStyles = {
  [TypographyVariant.TITLE]: {
    fontSize: '24px',
    lineHeight: '32px',
    fontWeight: 700,
  },

  [TypographyVariant.TITLE_SMALL]: {
    fontSize: '18px',
    lineHeight: '28px',
    fontWeight: 700,
  },

  [TypographyVariant.SUBTITLE]: {
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 700,
  },

  [TypographyVariant.SUBTITLE_SMALL]: {
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 500,
  },
  [TypographyVariant.SUBTITLE_SMALL_BOLD]: {
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 600,
  },
  [TypographyVariant.SUBTITLE_SMALL_BOLDER]: {
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 700,
  },

  [TypographyVariant.BODY]: {
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 400,
  },
  [TypographyVariant.BODY_BOLD]: {
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 500,
  },
  [TypographyVariant.BODY_BOLDER]: {
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 600,
  },
  [TypographyVariant.BODY_BOLDEST]: {
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 700,
  },

  [TypographyVariant.BODY_SMALL]: {
    fontSize: '13px',
    lineHeight: '18px',
    fontWeight: 400,
  },
  [TypographyVariant.BODY_SMALL_BOLD]: {
    fontSize: '13px',
    lineHeight: '18px',
    fontWeight: 500,
  },
  [TypographyVariant.BODY_SMALL_BOLDER]: {
    fontSize: '13px',
    lineHeight: '18px',
    fontWeight: 600,
  },
  [TypographyVariant.BODY_SMALL_BOLDEST]: {
    fontSize: '13px',
    lineHeight: '18px',
    fontWeight: 700,
  },

  [TypographyVariant.CAPTION]: {
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: 400,
  },
  [TypographyVariant.CAPTION_BOLD]: {
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: 500,
  },
  [TypographyVariant.CAPTION_BOLDER]: {
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: 600,
  },

  [TypographyVariant.CAPTION_SMALL]: {
    fontSize: '10px',
    lineHeight: '12px',
    fontWeight: 400,
  },
  [TypographyVariant.CAPTION_SMALL_BOLD]: {
    fontSize: '10px',
    lineHeight: '12px',
    fontWeight: 600,
  },
  [TypographyVariant.DISPLAY]: {
    fontSize: '32px',
    lineHeight: '40px',
    fontWeight: 700,
  },
  [TypographyVariant.CODE]: {
    fontSize: '12px',
    lineHeight: '20px',
    fontWeight: 400,
  },
} as const satisfies TypographySizeSemanticStyles;

const mobileTypographySizeSemanticStyles = {
  [MobileTypographyVariant.TITLE]: {
    fontSize: 24,
    lineHeight: 32,
    fontWeight: 500,
  },
  [MobileTypographyVariant.TITLE_BOLD]: {
    fontSize: 24,
    lineHeight: 32,
    fontWeight: 600,
  },
  [MobileTypographyVariant.TITLE_SMALL]: {
    fontSize: 18,
    lineHeight: 28,
    fontWeight: 600,
  },
  [MobileTypographyVariant.TITLE_SMALL_BOLD]: {
    fontSize: 18,
    lineHeight: 28,
    fontWeight: 700,
  },
  [MobileTypographyVariant.SUBTITLE]: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: 400,
  },
  [MobileTypographyVariant.SUBTITLE_BOLD]: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: 500,
  },
  [MobileTypographyVariant.SUBTITLE_BOLDER]: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: 600,
  },
  [MobileTypographyVariant.SUBTITLE_BOLDEST]: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: 700,
  },
  [MobileTypographyVariant.SUBTITLE_SMALL]: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 500,
  },
  [MobileTypographyVariant.SUBTITLE_SMALL_BOLD]: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 600,
  },
  [MobileTypographyVariant.SUBTITLE_SMALL_BOLDER]: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 700,
  },
  [MobileTypographyVariant.BODY]: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 400,
  },
  [MobileTypographyVariant.BODY_BOLD]: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 500,
  },
  [MobileTypographyVariant.BODY_BOLDER]: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 600,
  },
  [MobileTypographyVariant.BODY_BOLDEST]: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 700,
  },
  [MobileTypographyVariant.BODY_SMALL]: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: 400,
  },
  [MobileTypographyVariant.BODY_SMALL_BOLD]: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: 500,
  },
  [MobileTypographyVariant.BODY_SMALL_BOLDER]: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: 600,
  },
  [MobileTypographyVariant.BODY_SMALL_BOLDEST]: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: 700,
  },
  [MobileTypographyVariant.CAPTION]: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: 400,
  },
  [MobileTypographyVariant.CAPTION_BOLD]: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: 500,
  },
  [MobileTypographyVariant.CAPTION_BOLDER]: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: 600,
  },
  [MobileTypographyVariant.CAPTION_SMALL]: {
    fontSize: 10,
    lineHeight: 12,
    fontWeight: 400,
  },
  [MobileTypographyVariant.CAPTION_SMALL_BOLD]: {
    fontSize: 10,
    lineHeight: 12,
    fontWeight: 600,
  },
} as const satisfies MobileTypographySizeSemanticsStyles;

const lightTypographySemanticShades = {
  primary: colorPrimitive.gray['1000'],
  secondary: colorPrimitive.gray['900'],
  tertiary: colorPrimitive.gray['700'],
  disabled: colorPrimitive.gray['400'],

  inverse: colorPrimitive.gray.white,
  'inverse-secondary': colorPrimitive.gray['200'],

  'on-bg-fill': colorPrimitive.gray.white,
  'on-bg-fill-inverse': colorPrimitive.gray['1000'],
  'on-bg-fill-disabled': colorPrimitive.gray['400'],

  interactive: colorPrimitive.blue['700'],
  'interactive-hover': colorPrimitive.blue['800'],

  link: colorPrimitive.blue['600'],
  'link-hover': colorPrimitive.blue['700'],

  danger: colorPrimitive.red['600'],
  warning: colorPrimitive.orange['600'],
  success: colorPrimitive.green['600'],
  brand: colorPrimitive.purple['700'],
  code: colorPrimitive.red['700'],

  'text-selected': colorPrimitive.blurple['1000'],
  'text-unselected': colorPrimitive.gray['700'],
  'text-hover': colorPrimitive.gray['1000'],
} as const satisfies TypographyShadeSemanticStyles;

export const lightTypographySemanticStyles = {
  ...typographySizeSemanticStyles,
  shades: lightTypographySemanticShades,
} as const satisfies TypographySemanticStyles;

export const mobileLightTypographySemanticStyles = {
  ...mobileTypographySizeSemanticStyles,
  shades: lightTypographySemanticShades,
} as const satisfies MobileTypographySemanticStyles;

const darkTypographySemanticShades = {
  primary: colorPrimitive.gray['100'],
  secondary: colorPrimitive.gray['300'],
  tertiary: colorPrimitive.gray['400'],
  disabled: colorPrimitive.gray['700'],

  inverse: colorPrimitive.gray.black,
  'inverse-secondary': colorPrimitive.gray['900'],

  'on-bg-fill': colorPrimitive.gray.white,
  'on-bg-fill-inverse': colorPrimitive.gray['1000'],
  'on-bg-fill-disabled': colorPrimitive.gray['600'],

  interactive: colorPrimitive.blue['500'],
  'interactive-hover': colorPrimitive.blue['600'],

  link: colorPrimitive.blue['500'],
  'link-hover': colorPrimitive.blue['600'],

  danger: colorPrimitive.red['500'],
  warning: colorPrimitive.yellow['500'],
  success: colorPrimitive.green['500'],
  brand: colorPrimitive.blue['500'],
  code: colorPrimitive.red['500'],

  'text-selected': colorPrimitive.blurple['100'],
  'text-unselected': colorPrimitive.gray['400'],
  'text-hover': colorPrimitive.blurple['100'],
} as const satisfies TypographyShadeSemanticStyles;

export const darkTypographySemanticStyles = {
  ...typographySizeSemanticStyles,
  shades: darkTypographySemanticShades,
} as const satisfies TypographySemanticStyles;

export const mobileDarkTypographySemanticStyles = {
  ...mobileTypographySizeSemanticStyles,
  shades: darkTypographySemanticShades,
} as const satisfies MobileTypographySemanticStyles;
